/**
 * LaunchPrepButton — outbound SSO into Clinq's sibling app
 * (`prep.clinq.africa`, the financial model / funder readiness builder).
 *
 * Usage:
 *   <LaunchPrepButton prefillSector="Fintech" prefillStage="growth">
 *     Build your financial model
 *   </LaunchPrepButton>
 *
 * For LOGGED-IN founders we hit `/api/sso/launch-prep` which mints a
 * 5-min HS256 JWT and 302-redirects to `prep.clinq.africa/sso?...` —
 * the user lands authenticated, no second login.
 *
 * For ANONYMOUS visitors render <a href="https://prep.clinq.africa/?utm_source=clinq">
 * directly elsewhere in the marketing pages — Prep has its own signup.
 *
 * Props that map to optional partner pre-fills (all forwarded through
 * the backend so a refresh / share doesn't keep stale prefills around):
 *   prefillCompany, prefillSector, prefillStage, prefillCurrency,
 *   prefillRevenueModel, prefillFunder.
 *
 * `redirect` defaults to "/new" (Prep's new-model entry point) — pass
 * a different path if you want to drop the user elsewhere on Prep.
 */
import React from "react";

const _BACKEND = process.env.REACT_APP_BACKEND_URL;

export function LaunchPrepButton({
  prefillCompany,
  prefillSector,
  prefillStage,
  prefillCurrency,
  prefillRevenueModel,
  prefillFunder,
  prefillMissingDocs,  // array of doc-id strings, e.g. ["pitch_deck","financial_model"]
  redirect = "/new",
  className,
  children,
  testid = "launch-prep-btn",
  ...rest
}) {
  const onClick = () => {
    const params = new URLSearchParams({ redirect });
    if (prefillCompany)      params.set("prefill_company",       prefillCompany);
    if (prefillSector)       params.set("prefill_sector",        prefillSector);
    if (prefillStage)        params.set("prefill_stage",         prefillStage);
    if (prefillCurrency)     params.set("prefill_currency",      prefillCurrency);
    if (prefillRevenueModel) params.set("prefill_revenue_model", prefillRevenueModel);
    if (prefillFunder)       params.set("prefill_funder",        prefillFunder);
    if (Array.isArray(prefillMissingDocs) && prefillMissingDocs.length > 0) {
      params.set("prefill_missing_docs", JSON.stringify(prefillMissingDocs));
    }
    // Top-level navigation (NOT an XHR) so the browser follows the 302
    // redirect chain into prep.clinq.africa naturally.
    window.location.href = `${_BACKEND}/api/sso/launch-prep?${params.toString()}`;
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={className}
      {...rest}
    >
      {children}
    </button>
  );
}
